/**
 * Secure token storage utility that provides methods for securely managing authentication tokens
 * with different storage strategies based on browser capabilities.
 */

// Storage strategy interface
interface StorageStrategy {
  setItem(key: string, value: string): void;
  getItem(key: string): string | null;
  removeItem(key: string): void;
}

// Constants
const TOKEN_KEY = "auth_token";
const TOKEN_EXPIRY_KEY = "auth_token_expiry";
const REFRESH_TOKEN_KEY = "refresh_token";
const DEFAULT_EXPIRY_DAYS = 1; // Default token expiry in days

/**
 * Memory storage strategy - most secure but doesn't persist across page refreshes
 */
class MemoryStorageStrategy implements StorageStrategy {
  private storage: Record<string, string> = {};

  setItem(key: string, value: string): void {
    this.storage[key] = value;
  }

  getItem(key: string): string | null {
    return this.storage[key] || null;
  }

  removeItem(key: string): void {
    delete this.storage[key];
  }
}

/**
 * Session storage strategy - persists until browser tab is closed
 */
class SessionStorageStrategy implements StorageStrategy {
  setItem(key: string, value: string): void {
    try {
      sessionStorage.setItem(key, value);
    } catch (error) {
      console.error("SessionStorage setItem failed", { key, error });
    }
  }

  getItem(key: string): string | null {
    try {
      return sessionStorage.getItem(key);
    } catch (error) {
      console.error("SessionStorage getItem failed", { key, error });
      return null;
    }
  }

  removeItem(key: string): void {
    try {
      sessionStorage.removeItem(key);
    } catch (error) {
      console.error("SessionStorage removeItem failed", { key, error });
    }
  }
}

/**
 * LocalStorage strategy - persists even when browser is closed
 * Less secure but most convenient for users
 */
class LocalStorageStrategy implements StorageStrategy {
  setItem(key: string, value: string): void {
    try {
      localStorage.setItem(key, value);
    } catch (error) {
      console.error("LocalStorage setItem failed", { key, error });
    }
  }

  getItem(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch (error) {
      console.error("LocalStorage getItem failed", { key, error });
      return null;
    }
  }

  removeItem(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error("LocalStorage removeItem failed", { key, error });
    }
  }
}

/**
 * Secure token storage with expiration support
 */
class TokenStorage {
  private storageStrategy: StorageStrategy;

  constructor() {
    // Select the most secure available storage strategy
    if (typeof window === "undefined") {
      // For server-side rendering, use memory storage
      this.storageStrategy = new MemoryStorageStrategy();
      console.info("Using memory storage strategy (SSR mode)");
    } else {
      try {
        // PRIORITIZE localStorage for cross-tab persistence
        if (
          window.localStorage &&
          typeof localStorage.getItem === "function"
        ) {
          this.storageStrategy = new LocalStorageStrategy();
          console.debug("Using local storage strategy");
        } else if (
          window.sessionStorage &&
          typeof sessionStorage.getItem === "function"
        ) {
          this.storageStrategy = new SessionStorageStrategy();
          console.debug("Using session storage strategy"); // Less likely to be used now
        } else {
          this.storageStrategy = new MemoryStorageStrategy();
          console.warn("Fallback to memory storage strategy - token will not persist page refreshes");
        }
      } catch (e) {
        // Fallback to memory storage if browser storage is disabled
        this.storageStrategy = new MemoryStorageStrategy();
        console.warn("Browser storage access error, using memory storage strategy", e);
      }
    }
  }

  /**
   * Store authentication token with expiration
   */
  setToken(token: string, expiryDays: number = DEFAULT_EXPIRY_DAYS): void {
    if (!token) {
      console.warn("Attempted to store empty token");
      return;
    }

    try {
      // Store the token
      this.storageStrategy.setItem(TOKEN_KEY, token);

      // Calculate and store expiration time
      const expiryDate = new Date();
      expiryDate.setDate(expiryDate.getDate() + expiryDays);
      this.storageStrategy.setItem(TOKEN_EXPIRY_KEY, expiryDate.toISOString());

      console.info("Authentication token stored successfully", { expiryDate });
    } catch (error) {
      console.error("Failed to set token", error);
    }
  }

  /**
   * Store refresh token
   */
  setRefreshToken(refreshToken: string): void {
    if (!refreshToken) {
      console.warn("Attempted to store empty refresh token");
      return;
    }

    try {
      this.storageStrategy.setItem(REFRESH_TOKEN_KEY, refreshToken);
      console.info("Refresh token stored successfully");
    } catch (error) {
      console.error("Failed to set refresh token", error);
    }
  }

  /**
   * Get stored refresh token
   */
  getRefreshToken(): string | null {
    try {
      return this.storageStrategy.getItem(REFRESH_TOKEN_KEY);
    } catch (error) {
      console.error("Failed to get refresh token", error);
      return null;
    }
  }

  /**
   * Retrieve the stored authentication token if valid
   */
  getToken(): string | null {
    try {
      // Check if token is expired
      const expiryStr = this.storageStrategy.getItem(TOKEN_EXPIRY_KEY);
      if (expiryStr) {
        const expiry = new Date(expiryStr);
        const now = new Date();

        // Check if token is expired or about to expire (within 5 minutes)
        const fiveMinutes = 5 * 60 * 1000;
        if (now > expiry) {
          // Token expired, remove it
          console.info("Token expired, removing");
          this.removeToken();
          return null;
        } else if (expiry.getTime() - now.getTime() < fiveMinutes) {
          // Token about to expire, log warning
          console.warn("Token is about to expire", {
            expiryTime: expiry,
            timeLeft:
              Math.floor((expiry.getTime() - now.getTime()) / 1000) +
              " seconds",
          });
        }
      } else {
        console.debug("No token expiry found");
      }

      // Return the token if not expired
      return this.storageStrategy.getItem(TOKEN_KEY);
    } catch (error) {
      console.error("Failed to get token", error);
      return null;
    }
  }

  /**
   * Remove stored token and expiration
   */
  removeToken(): void {
    try {
      this.storageStrategy.removeItem(TOKEN_KEY);
      this.storageStrategy.removeItem(TOKEN_EXPIRY_KEY);
      this.storageStrategy.removeItem(REFRESH_TOKEN_KEY);
      console.info("All tokens and expiry data removed");
    } catch (error) {
      console.error("Failed to remove token", error);
    }
  }

  /**
   * Check if token exists and is valid
   */
  hasValidToken(): boolean {
    return this.getToken() !== null;
  }

  /**
   * Get token expiration date
   */
  getTokenExpiry(): Date | null {
    try {
      const expiryStr = this.storageStrategy.getItem(TOKEN_EXPIRY_KEY);
      return expiryStr ? new Date(expiryStr) : null;
    } catch (error) {
      console.error("Failed to get token expiry", error);
      return null;
    }
  }

  /**
   * Get time until token expiration in seconds
   */
  getSecondsUntilExpiry(): number | null {
    const expiry = this.getTokenExpiry();
    if (!expiry) return null;

    const now = new Date();
    const diffMs = expiry.getTime() - now.getTime();
    return diffMs > 0 ? Math.floor(diffMs / 1000) : 0;
  }
}

// Export a singleton instance
export const tokenStorage = new TokenStorage();
